import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Callout, EmptyState, Field, buttonClass, inputClass } from '@/components/ui';

import { Badge } from '@/components/ui';
import { METHOD_TO_MACHINE, boardToday, isOverdue, type MachineState } from '@/lib/data/jobs';
import { adminMachinesQuery, tenantQuery } from '@/queries';

/**
 * Machines. Spec 002 US-7 / AC-7.5.
 *
 * Each machine is a record with a type, so "assign a job to a printer" can be
 * checked rather than trusted. A job only reaches a machine whose type runs its
 * decoration method.
 */

const STATE_TONE: Record<MachineState, 'ok' | 'brand' | 'warn'> = {
  available: 'ok',
  running: 'brand',
  maintenance: 'warn',
};

const STATE_LABEL: Record<MachineState, string> = {
  available: 'Available',
  running: 'Running',
  maintenance: 'Maintenance',
};


/**
 * Rename a machine, change its capacity, or take it off the board.
 *
 * `POST /machines` was the whole module. A machine typed in with the wrong
 * name was permanent: no rename, no capacity change, and no way to retire it.
 * The studio's answer to that is a second machine with the right name and a
 * ghost beside it for ever, both showing on the board. R-243.
 *
 * Switched off, never deleted. A machine that has run jobs is referenced by
 * every one of them, and removing the row would orphan the record of who ran
 * what.
 */
function MachineEditor({ machine }: {
  machine: { id: string; name: string; capacity_per_day: number; state: string; active?: boolean };
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(machine.name);
  const [capacity, setCapacity] = useState(String(machine.capacity_per_day));
  const [said, setSaid] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const res = await fetch(`/api/machines/${machine.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not save it.');
    },
    onSuccess: () => {
      setSaid(null);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'machines'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={buttonClass('secondary', 'sm')}>
        Edit
      </button>
    );
  }

  return (
    <span className="absolute right-5 z-10 w-[280px] rounded-[10px] border border-line-strong bg-panel p-3 shadow-[var(--shadow-2)]">
      <label className="block">
        <span className="mb-1 block text-[12px] font-semibold">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} w-full`} />
      </label>
      <label className="mt-2 block">
        <span className="mb-1 block text-[12px] font-semibold">Units a day</span>
        <input
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          inputMode="numeric"
          className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
        />
      </label>

      {said && <p className="mt-2 rounded-[8px] border border-line p-2 text-[12px]">{said}</p>}

      <span className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={buttonClass('primary', 'sm')}
          disabled={save.isPending}
          onClick={() => save.mutate({ name, capacity_per_day: Number(capacity) })}
        >
          Save
        </button>
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          disabled={save.isPending}
          onClick={() => save.mutate({
            state: machine.state === 'maintenance' ? 'available' : 'maintenance',
          })}
        >
          {machine.state === 'maintenance' ? 'Back in service' : 'Under maintenance'}
        </button>
        <button
          type="button"
          className={buttonClass('secondary', 'sm')}
          disabled={save.isPending}
          onClick={() => save.mutate({ active: machine.active === false })}
        >
          {machine.active === false ? 'Bring it back' : 'Retire it'}
        </button>
        <button type="button" className={buttonClass('tertiary', 'sm')} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </span>
    </span>
  );
}

/**
 * Recording a machine, without a developer. D020.
 *
 * The empty state told Guy to send the list, which is right for the day the
 * workshop is set up and useless for the replacement press that arrives in
 * October. R-197.
 */
function AddMachine() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('screen');
  const [capacity, setCapacity] = useState('');
  const [down, setDown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/machines', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name, type,
          capacity_per_day: Number(capacity),
          state: down ? 'maintenance' : 'available',
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setName(''); setCapacity(''); setDown(false); setError(null); setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'machines'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setOpen(true)}>
        Add a machine
      </button>
    );
  }

  return (
    <form
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_160px_160px]">
        <Field label="Name" htmlFor="m-name" hint="What the board calls it">
          <input
            id="m-name" className={inputClass} value={name} required
            onChange={(e) => setName(e.target.value)} placeholder="Press 1"
          />
        </Field>
        <Field label="Type" htmlFor="m-type" hint="Decides which jobs reach it">
          <select
            id="m-type" className={inputClass} value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="screen">Screen print</option>
            <option value="dtf">DTF</option>
            <option value="embroidery">Embroidery</option>
            <option value="sublimation">Sublimation</option>
          </select>
        </Field>
        <Field label="Units a day" htmlFor="m-cap" hint="Used to show load">
          <input
            id="m-cap" className={inputClass} value={capacity} required
            inputMode="numeric" onChange={(e) => setCapacity(e.target.value)}
            placeholder="400"
          />
        </Field>
      </div>

      <label className="mt-3 flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={down} onChange={(e) => setDown(e.target.checked)} />
        {/* A replacement often arrives broken, or arrives before the part does. */}
        It is not usable yet
      </label>

      {error && <Callout tone="danger" title={error} className="mt-3" />}

      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={add.isPending}>
          {add.isPending ? 'Adding' : 'Add machine'}
        </button>
        <button
          type="button" className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setError(null); }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminMachinesPage() {
  // `adminMachinesQuery`, not `adminJobsQuery`: this screen sees retired
  // machines so one can be brought back, and the board must not (R-244).
  const { jobs, machines } = useSuspenseQuery(adminMachinesQuery).data;
  // The workshop's zone, and one "today" for every machine on the screen. The
  // late count here and the one on the job board must agree (R-232).
  const zone = useSuspenseQuery(tenantQuery).data.timezone;
  const today = boardToday(zone);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Machines</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          What each machine can run, what is on it now, and how loaded it is.
        </p>
      </div>

      {/* An empty screen must say what is missing and who fills it. R-172.
          Guy opens this before the trade show; a blank grid tells him nothing
          and looks like a fault. */}
      {machines.length === 0 && (
        <div className="mb-5">
          <EmptyState
            title="No machines yet"
            body="Send the machine list and the whole workshop loads in one command.
                  Add one here whenever a machine arrives or is replaced. Until there
                  is at least one, the job board has nothing to assign work to and
                  every card shows as unassigned."
          />
          <div className="mt-4 flex justify-center">
            <AddMachine />
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {machines.map((machine) => {
          const queue = jobs.filter(
            (j) => j.machine_id === machine.id && j.stage !== 'complete',
          );
          const units = queue.reduce((sum, j) => sum + j.qty, 0);
          // `capacity_per_day` is refused at zero when a machine is created or
          // edited, but a row seeded before that check, or loaded by a script,
          // can still hold one. Dividing by it puts NaN% on the load bar, and a
          // screen with an impossible number on it makes the correct numbers
          // beside it look wrong too. R-268.
          const load = machine.capacity_per_day > 0
            ? Math.min(100, Math.round((units / machine.capacity_per_day) * 100))
            : 0;
          const late = queue.filter((j) => isOverdue(j, today, zone)).length;

          const runs = Object.entries(METHOD_TO_MACHINE)
            .filter(([, type]) => type === machine.type)
            .map(([method]) => method);

          return (
            <article
              key={machine.id}
              className={'relative rounded-[10px] border border-line bg-panel p-5 '
                + (machine.active === false ? 'opacity-60' : '')}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold tracking-[-0.02em]">{machine.name}</h2>
                  <p className="mt-0.5 text-[13px] capitalize text-muted">
                    Runs {runs.join(', ')}
                  </p>
                </div>
                <span className="flex items-center gap-2">
                  {machine.active === false
                    ? <Badge tone="neutral">Retired</Badge>
                    : <Badge tone={STATE_TONE[machine.state]}>{STATE_LABEL[machine.state]}</Badge>}
                  <MachineEditor machine={machine} />
                </span>
              </div>

              <dl className="mt-5 flex gap-6">
                <div>
                  <dt className="text-[12px] text-muted">In the queue</dt>
                  <dd className="font-mono text-2xl font-extrabold">{queue.length}</dd>
                </div>
                <div>
                  <dt className="text-[12px] text-muted">Units</dt>
                  <dd className="font-mono text-2xl font-extrabold">{units}</dd>
                </div>
                <div>
                  <dt className="text-[12px] text-muted">A day</dt>
                  <dd className="font-mono text-2xl font-extrabold text-muted">
                    {machine.capacity_per_day}
                  </dd>
                </div>
              </dl>

              <div className="mt-4">
                <div className="flex items-baseline justify-between text-[12px]">
                  <span className="text-muted">Load against a day</span>
                  <span className="font-mono font-bold">{load}%</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
                  <div
                    className={load > 90 ? 'h-full bg-danger' : 'h-full bg-brand'}
                    style={{ width: `${load}%` }}
                  />
                </div>
              </div>

              {queue.length > 0 && (
                <ul className="mt-4 space-y-1.5 border-t border-line pt-4">
                  {queue.slice(0, 3).map((j) => (
                    <li key={j.id} className="flex justify-between gap-3 text-[13px]">
                      <span className="truncate">
                        <span className="font-mono font-bold text-brand-ink">{j.ref}</span>{' '}
                        <span className="text-muted">{j.customer}</span>
                      </span>
                      <span className="shrink-0 text-muted">{j.qty}</span>
                    </li>
                  ))}
                </ul>
              )}

              {late > 0 && (
                <p className="mt-3 text-[13px] font-semibold text-danger">
                  {late} job{late === 1 ? '' : 's'} past due on this machine
                </p>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
